/**
 * Frontend mirror of the activity-type vocabulary the Einstein API
 * emits to the Activity Ledger. Source of truth lives at
 * `src/Galileo.Einstein.Api/Common/ActivityLedger/EinsteinActivityTypes.cs`;
 * this file must be kept in lockstep so the Timeline view's per-type
 * renderers stay exhaustive.
 *
 * The contract with the BFF is intentionally tolerant: unknown
 * activity types are still rendered (with a generic fallback row)
 * rather than dropped, so a producer that adds a new constant on the
 * server before this file catches up never silently disappears from
 * the audit trail. Treat any unknown-type row in the UI as a signal
 * to update this list.
 */

export const KNOWN_TIMELINE_ACTIVITY_TYPES = [
    // Workflow lifecycle
    "workflow.created",
    "workflow.child.spawned",
    "workflow.edited",

    // Work item lifecycle
    "workitem.created",
    "workitem.routed",
    "workitem.broadcasted",
    "workitem.addressed",
    "workitem.replied",
    "workitem.completed",
    "workitem.completed.by-reply",
    "workitem.queues.changed",
    "workitem.edited",
    "workitem.removed",
    "workitem.approved",
    "workitem.rejected",
    "workitem.snoozed",
    "workitem.rescheduled",
    "workitem.viewed",

    // Queue lifecycle and staffing
    "queue.created",
    "queue.renamed",
    "queue.reparented",
    "queue.deactivation.started",
    "queue.deactivation.blocked",
    "queue.deactivated",
    "queue.reactivated",
    "queue.member.added",
    "queue.member.removed",
    "queue.accessed.via-admin-role",
    "queue.accessed.via-management-group-admin",
    "queue.owner.changed",
    "managementgroup.team.pushed-to-queue",
    "managementgroup.team.withdrawn-from-queue",
] as const;

export type KnownTimelineActivityType = (typeof KNOWN_TIMELINE_ACTIVITY_TYPES)[number];

const KNOWN = new Set<string>(KNOWN_TIMELINE_ACTIVITY_TYPES);

export function isKnownTimelineActivityType(value: string): value is KnownTimelineActivityType {
    return KNOWN.has(value);
}

/**
 * Mirror of `EinsteinEntityTypes` for entity kinds rendered by the shared
 * Timeline view. Routing rules and management groups do not currently have
 * timeline surfaces; unknown entity types still fall back safely.
 */
export const TIMELINE_ENTITY_TYPES = {
    workflow: "workflow",
    workItem: "workitem",
    queue: "queue",
} as const;
